import { Element, Frame, useEditor } from "@craftjs/core";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { IconButton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Cancel from "../../assets/icons/Cancel";
import { EmptyContainerWidget } from "../../components/SegmentBuilder/CodeBlocks";
import useScreenSize from "../../hooks/useScreenSize";
import { SegmentProvider } from "../../pages/ViewWheel/SegmentContext";
import { patterns } from "../SegmentBuilder/Shared/Wallpaper/Wallpaper.styled";
import TextView from "../TextView";
import "./field.scss";
import * as Styled from "./styles";
import { isValidSuggestedColor, getSuggestedColor } from "../../utils/helpers";
import {
  getSelectedMenu,
  getSelectedSegment,
  getSelectedSegmentCharCount,
} from "../../redux/selectors/segmentBuilderSelectors";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { handleMenuChange } from "../../redux/reducers/segmentBuilder";
import { usePaletteColor } from "../../hooks/usePaletteColor";
import { charLimitInASegment } from "../SegmentSettings";
import { Box, Typography } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

const SegmentView = ({
  selectedArc = {},
  onClose,
  onClickNext,
  onClickPrev,
  openSegmentList,
  showBackIcon = false,
  onClickBack = () => {},
  isBuilder = false,
  height,
  isPreview = false,
}) => {
  const { settings: segmentSettings = {} } = selectedArc;
  const dispatch = useDispatch();
  const {
    wallpaperTransparency,
    wallpaperOverlayColor,
    bgWallpaper,
    isPattern,
    title,
    color,
    segmentBgColor,
    currentBg,
    patternColor,
    patternId,
  } = segmentSettings;

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor } = usePaletteColor();
  const selectedSegmentCharCount = useSelector(getSelectedSegmentCharCount);

  const { connectors, actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { isMobilePortrait, portraitScreenRatio } = useScreenSize();

  const ref = useRef();
  const swipeRef = useRef(null);

  // useSwipe(swipeRef, handleSwipeUp, handleSwipeDown);

  useEffect(() => {
    if (selectedArc?.nodes) {
      try {
        actions.deserialize(selectedArc.nodes);
      } catch (error) {
        console.error("Error deserializing nodes:", error);
      }
    }
  }, [selectedArc?.id]);

  // Check if segment is empty (no content blocks)
  const isSegmentEmpty = () => {
    if (!selectedArc?.nodes) return true;

    try {
      const nodeMap =
        typeof selectedArc.nodes === "string"
          ? JSON.parse(selectedArc.nodes)
          : selectedArc.nodes;

      const rootNode = nodeMap?.ROOT;
      return !rootNode?.nodes || rootNode.nodes.length === 0;
    } catch (error) {
      return true;
    }
  };

  const selectedMenu = useSelector(getSelectedMenu);

  return (
    <Styled.MainWrapper
      ref={ref}
      viewWidth={portraitScreenRatio >= 1.5 ? "100vw" : "80vw"}
      segmentListOpen={openSegmentList}
      isMobilePortrait={isMobilePortrait}
    >
      {isMobilePortrait && (
        <Styled.Navigator>
          <Styled.IconView onClick={onClickPrev} style={{ width: 36 }}>
            <ArrowCircleLeftIcon style={{ width: 24, height: 24 }} />
          </Styled.IconView>
          {showBackIcon && (
            <Styled.IconView
              onClick={onClickBack}
              style={{ width: "max-content", height: 32 }}
            >
              <KeyboardDoubleArrowUpIcon /> <TextView>Back to wheel</TextView>
            </Styled.IconView>
          )}
          <Styled.IconView onClick={onClickNext} style={{ width: 36 }}>
            <ArrowCircleRightIcon style={{ width: 24, height: 24 }} />
          </Styled.IconView>
        </Styled.Navigator>
      )}
      <Styled.Container
        isMobilePortrait={isMobilePortrait}
        color={getPaletteColor(color)}
        currentBg={currentBg}
        segmentBgColor={
          currentBg == 2
            ? `${segmentBgColor} 1`
            : getPaletteColor(segmentBgColor)
        }
        bgWallpaper={bgWallpaper}
        isPattern={isPattern}
        wallpaperOverlayColor={wallpaperOverlayColor}
        viewWidth={portraitScreenRatio >= 1.5 ? "94vw" : "74vw"}
        height={height}
      >
        {selectedArc?.settings?.icon && (
          <Styled.SegmentIcon color={getPaletteColor(color)}>
            <IconButton m={2} sx={{ color: "white", fontSize: "18px" }}>
              {selectedArc?.settings?.icon}
            </IconButton>
          </Styled.SegmentIcon>
        )}

        <Styled.HeaderContainer
          ref={isMobilePortrait ? swipeRef : null}
          onClick={() => isBuilder && dispatch(handleMenuChange({ num: 2 }))}
        >
          <Styled.InnerContainer color={getPaletteColor(color)}>
            <TextView
              variant="subtitle"
              style={{ fontWeight: 700, WebkitLineClamp: 1 }}
            >
              {title}
            </TextView>
            <Styled.Row>
              {/* {isMobilePortrait && (
                <Styled.IconView
                  style={{ padding: 5, marginRight:10 }}
                  onClick={toggleFullView}
                >
                  {fullView ? <Remove /> : <Expand />}
                </Styled.IconView>
              )} */}
              <Styled.IconView style={{ padding: 5 }} onClick={onClose}>
                <Cancel />
              </Styled.IconView>
            </Styled.Row>
          </Styled.InnerContainer>
        </Styled.HeaderContainer>

        {/* Character limit warning banner */}
        {isBuilder && selectedSegmentCharCount > charLimitInASegment && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#FFF4F4",
              border: "1px solid #FF6B6B",
              borderRadius: "4px",
              padding: "8px 12px",
              margin: "8px 16px",
            }}
          >
            <WarningIcon sx={{ color: "#FF6B6B", fontSize: "20px" }} />
            <Typography
              sx={{
                fontSize: "12px",
                color: "#FF6B6B",
                fontFamily: "DM Sans",
                lineHeight: "16px",
              }}
            >
              Character limit exceeded ({selectedSegmentCharCount}/
              {charLimitInASegment}). You must reduce content before saving.
            </Typography>
          </Box>
        )}
        <div
          style={{ position: "relative" }}
          ref={(ref) => connectors.select(connectors.hover(ref, null), null)}
          onClick={(e) => {
            if (!isPreview) {
              dispatch(handleMenuChange({ num: 3 }));
            }
            e.stopPropagation();
          }}
        >
          {currentBg === 2 ? (
            <Styled.BackgroundImageCover
              currentBg={currentBg}
              bgWallpaper={bgWallpaper}
              wallpaperTransparency={wallpaperTransparency}
            />
          ) : null}

          {currentBg === 1 && patternId > 0 && patternId !== 0 && (
            <Styled.PatternContainer
              wallpaperTransparency={wallpaperTransparency}
              wallpaperOverlayColor={wallpaperOverlayColor}
            >
              <div style={{ marginTop: "-100%" }}>
                {patterns.find((pattern) => pattern.id === patternId)
                  ?.component &&
                  React.createElement(
                    patterns.find((pattern) => pattern.id === patternId)
                      .component,
                    { color: getPaletteColor(patternColor) }
                  )}
              </div>
            </Styled.PatternContainer>
          )}
          {currentBg === 1 && patternId === 0 && (
            <Styled.PatternContainer
              wallpaperTransparency={wallpaperTransparency}
              wallpaperOverlayColor={"white"}
            ></Styled.PatternContainer>
          )}
          <Styled.ScrollableContainer
            isMobilePortrait={isMobilePortrait}
            viewWidth={portraitScreenRatio >= 1.5 ? "94vw" : "74vw"}
            style={{
              overflowY: isSegmentEmpty() ? "hidden" : "auto",
              position: "relative",
            }}
          >
            <Styled.ContentContainer
              style={{
                minHeight: isSegmentEmpty() ? "auto" : "100%",
              }}
            >
              {!isBuilder && isSegmentEmpty() ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "65vh",
                    color: "#666",
                    fontSize: "16px",
                    fontFamily: "DM Sans",
                    textAlign: "center",
                  }}
                >
                  This segment is empty
                </Box>
              ) : (
                <SegmentProvider selectedArc={selectedArc}>
                  <Frame>
                    <Element
                      canvas
                      is={EmptyContainerWidget}
                      data-cy="root-container"
                      style={{
                        minHeight: "calc(100vh - 200px)",
                        width: "100%",
                      }}
                    ></Element>
                  </Frame>
                </SegmentProvider>
              )}
            </Styled.ContentContainer>
          </Styled.ScrollableContainer>
        </div>
      </Styled.Container>
    </Styled.MainWrapper>
  );
};

export default SegmentView;
